import React, { createContext, useContext, useState, useEffect } from 'react';

interface AuthContextType {
  token: string | null;
  user: { name: string; email: string; picture: string } | null;
  login: (credential: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

function parseJwt(token: string) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('ehr_auth_token'));
  const [user, setUser] = useState<AuthContextType['user']>(() => {
    const stored = localStorage.getItem('ehr_auth_token');
    if (!stored) return null;
    const payload = parseJwt(stored);
    if (!payload || payload.exp * 1000 < Date.now()) {
      localStorage.removeItem('ehr_auth_token');
      return null;
    }
    return { name: payload.name, email: payload.email, picture: payload.picture };
  });

  const login = (credential: string) => {
    const payload = parseJwt(credential);
    if (!payload) return;
    localStorage.setItem('ehr_auth_token', credential);
    setToken(credential);
    setUser({ name: payload.name, email: payload.email, picture: payload.picture });
  };

  const logout = () => {
    localStorage.removeItem('ehr_auth_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ token, user, login, logout, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
